import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';

/**
 * The scene address, with Google suggestions underneath it.
 *
 * IT IS A TEXT BOX FIRST AND AN AUTOCOMPLETE SECOND. Whatever is typed is saved
 * on every keystroke exactly as typed, and a suggestion only ever replaces it if
 * someone picks one. That is the opposite of most address widgets, and it is the
 * right way round here: a caller says "the N3 northbound just past the Buccleuch
 * interchange, behind the barrier", which Google has never heard of, and an
 * autocomplete that refuses to accept an unmatched address would make that job
 * untypeable at the exact moment it matters.
 *
 * Picking a suggestion also sets coordinates, which is what the CCO map link and
 * the nearest-vehicle ranking run on. Typing a description leaves them null and
 * everything still works, just without a pin.
 *
 * ONE SESSION TOKEN PER ADDRESS. Google bills autocomplete per session rather
 * than per keystroke; the token groups a burst of typing and the final details
 * lookup into a single charge. A new one is minted after each selection, because
 * a session ends when it is used.
 */

interface Suggestion {
  placeId: string;
  primary: string;
  secondary: string;
}

export interface ResolvedPlace {
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
}

/** Long enough not to collide, short enough for a URL. */
function newSessionToken(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

export function AddressField({
  label,
  value,
  placeholder,
  onChange,
  onResolve,
  onBlur,
}: {
  label: string;
  value: string;
  placeholder?: string;
  /** Every keystroke, verbatim. */
  onChange: (value: string) => void;
  /** Only when a suggestion is chosen — carries coordinates. */
  onResolve: (place: ResolvedPlace) => void;
  onBlur?: () => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const sessionToken = useRef(newSessionToken());
  const boxRef = useRef<HTMLDivElement>(null);
  // Rising counter so a slow response for "12 Riv" cannot overwrite the
  // suggestions for "12 Rivonia Road" typed after it.
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!open || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    // 250ms: below a comfortable typing cadence, so a fast typist makes one
    // request rather than one per character — which is both the latency and the
    // bill.
    const seq = ++requestSeq.current;
    const timer = setTimeout(() => {
      void api
        .get<{ suggestions: Suggestion[] }>(
          `/places/autocomplete?q=${encodeURIComponent(value)}&sessionToken=${sessionToken.current}`,
        )
        .then((res) => {
          if (seq !== requestSeq.current) return;
          setSuggestions(res.suggestions);
          setHighlight(-1);
        })
        .catch(() => {
          // Silent. The field still works as a text box, and an error toast for
          // a failed suggestion lookup during an emergency call is noise.
          if (seq === requestSeq.current) setSuggestions([]);
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [value, open]);

  // Close on an outside click, so the list does not sit over the next field.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  async function choose(s: Suggestion) {
    setOpen(false);
    setSuggestions([]);
    // Optimistic: show the chosen text immediately rather than after a round
    // trip. If the details lookup fails, this is still a better address than
    // what was half-typed.
    const optimistic = [s.primary, s.secondary].filter(Boolean).join(', ');
    onChange(optimistic);

    try {
      const place = await api.get<ResolvedPlace>(
        `/places/${encodeURIComponent(s.placeId)}?sessionToken=${sessionToken.current}`,
      );
      onResolve({
        formattedAddress: place.formattedAddress || optimistic,
        latitude: place.latitude,
        longitude: place.longitude,
      });
    } catch {
      onResolve({ formattedAddress: optimistic, latitude: null, longitude: null });
    } finally {
      // The session is spent; the next address starts a new one.
      sessionToken.current = newSessionToken();
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      // Only with something highlighted. Enter on free text must submit what was
      // typed, not silently swap in Google's first guess — which on a partial
      // address is often a different suburb entirely.
      e.preventDefault();
      void choose(suggestions[highlight]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-xs text-slate-400">{label}</label>
      <input
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        className="mt-1 w-full rounded border border-line bg-surface-2 px-2 py-1.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-sky-600 focus:outline-none"
      />

      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-surface-3 shadow-xl">
          {suggestions.map((s, i) => (
            <li key={s.placeId}>
              <button
                type="button"
                // onMouseDown, not onClick: blur fires first on click and would
                // close the list before the selection registered.
                onMouseDown={(e) => {
                  e.preventDefault();
                  void choose(s);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`block w-full px-3 py-2 text-left text-sm ${
                  i === highlight ? 'bg-sky-600/20 text-slate-100' : 'text-slate-300'
                }`}
              >
                <span className="block">{s.primary}</span>
                <span className="block text-xs text-slate-500">{s.secondary}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
